import React, { useEffect, useState, type FC } from "react";
import classes from "./SectionDragCards.module.css";
import { TrendingArrayType } from "../../Routes/Home";
import { motion } from "framer-motion";
import SkeletonCardsGrid from "../SkeletonCardsGrid";
import { useAppDispatch, useAppSelector } from "../../Store/hooks";
import {
  setAdventureMovies,
  setAnimationMovies,
  setComedyMovies,
  setDocumentaryMovies,
  setMysteryMovies,
  setScificMovies,
  setThrillerMovies,
  setTrendingMovies,
  setWesternMovies,
} from "../../Slices/mediaTypeSlice";
import MovieCard, { MovieData } from "./MovieCard";
import ErrorCard from "./ErrorCard";
import DragCardsTitle from "./DragCardsTitle";
import { setMovieGenre } from "../../Slices/movieGenreSlice";
import { useNavigate } from "react-router-dom";
import { setSeriesGenre } from "../../Slices/seriesGenreSlice";

export type fetchedDataType = {
  result: {
    page: number;
    results: TrendingArrayType;
    total_pages: number;
    total_results: number;
  };
};

export type Error = {
  data?: string;
  error?: string;
  originalStatus?: number;
  status?: string;
};

type SectionDragCardsProps = {
  trending: fetchedDataType;
  adventure: fetchedDataType;
  comedy: fetchedDataType;
  scific: fetchedDataType;
  thriller: fetchedDataType;
  animation: fetchedDataType;
  mystery: fetchedDataType;
  documentary: fetchedDataType;
  western: fetchedDataType;
  trendingLoading: boolean;
  adventureLoading: boolean;
  comedyLoading: boolean;
  scificLoading: boolean;
  thrillerLoading: boolean;
  animationLoading: boolean;
  mysteryLoading: boolean;
  documentaryLoading: boolean;
  westernLoading: boolean;
  trendingError: unknown | Error;
  adventureError: unknown | Error;
  comedyError: unknown | Error;
  scificError: unknown | Error;
  thrillerError: unknown | Error;
  animationError: unknown | Error;
  mysteryError: unknown | Error;
  documentaryError: unknown | Error;
  westernError: unknown | Error;
  style?: React.CSSProperties;
  hideFilter: boolean;
  checkAndNavigate: boolean;
  navigateToMovies: boolean;
  navigateToSeries: boolean;
};

const SectionDragCards: FC<SectionDragCardsProps> = ({
  trending,
  adventure,
  comedy,
  scific,
  thriller,
  animation,
  documentary,
  mystery,
  western,
  trendingLoading,
  adventureLoading,
  comedyLoading,
  scificLoading,
  thrillerLoading,
  animationLoading,
  mysteryLoading,
  documentaryLoading,
  westernLoading,
  trendingError,
  adventureError,
  comedyError,
  thrillerError,
  animationError,
  documentaryError,
  mysteryError,
  scificError,
  westernError,
  style,
  hideFilter,
  checkAndNavigate,
  navigateToMovies,
  navigateToSeries,
}) => {
  const [trendingContraint, setTrendingConstraint] = useState<number>(0);
  const [adventureContraint, setAdventureConstraint] = useState<number>(0);
  const [comedyContraint, setComedyConstraint] = useState<number>(0);
  const [scificContraint, setScificConstraint] = useState<number>(0);
  const [thrillerContraint, setThrillerConstraint] = useState<number>(0);

  const [animationContraint, setAnimationConstraint] = useState<number>(0);
  const [mysteryContraint, setMysteryConstraint] = useState<number>(0);
  const [documentaryContraint, setDocumentaryConstraint] = useState<number>(0);
  const [westernContraint, setWesternConstraint] = useState<number>(0);

  useEffect(() => {
    const updateConstraints = () => {
      // logic to implement the drag to upto how much screen
      // padding from screen sides + total cards * width of card + total cards * gap between cards + total cards * box shadow twice

      if (window.innerWidth <= 800) {
        setTrendingConstraint(
          10 +
            trending?.result?.results.length * 150 +
            trending?.result?.results.length * 10 +
            trending?.result?.results.length * 1 -
            window.innerWidth
        );
        setAdventureConstraint(
          10 +
            adventure?.result?.results.length * 150 +
            adventure?.result?.results.length * 10 +
            adventure?.result?.results.length * 1 -
            window.innerWidth
        );
        setComedyConstraint(
          10 +
            comedy?.result?.results.length * 150 +
            comedy?.result?.results.length * 10 +
            comedy?.result?.results.length * 1 -
            window.innerWidth
        );
        setScificConstraint(
          10 +
            scific?.result?.results.length * 150 +
            scific?.result?.results.length * 10 +
            scific?.result?.results.length * 1 -
            window.innerWidth
        );
        setThrillerConstraint(
          10 +
            thriller?.result?.results.length * 150 +
            thriller?.result?.results.length * 10 +
            thriller?.result?.results.length * 1 -
            window.innerWidth
        );

        setAnimationConstraint(
          10 +
            animation?.result?.results.length * 150 +
            animation?.result?.results.length * 10 +
            animation?.result?.results.length * 1 -
            window.innerWidth
        );

        setMysteryConstraint(
          10 +
            mystery?.result?.results.length * 150 +
            mystery?.result?.results.length * 10 +
            mystery?.result?.results.length * 1 -
            window.innerWidth
        );

        setDocumentaryConstraint(
          10 +
            documentary?.result?.results.length * 150 +
            documentary?.result?.results.length * 10 +
            documentary?.result?.results.length * 1 -
            window.innerWidth
        );

        setWesternConstraint(
          10 +
            western?.result?.results.length * 150 +
            western?.result?.results.length * 10 +
            western?.result?.results.length * 1 -
            window.innerWidth
        );
      } else {
        setTrendingConstraint(
          window.innerWidth * 0.05 +
            trending?.result?.results.length * 150 +
            trending?.result?.results.length * 10 +
            trending?.result?.results.length * 1 -
            window.innerWidth
        );
        setAdventureConstraint(
          window.innerWidth * 0.05 +
            adventure?.result?.results.length * 150 +
            adventure?.result?.results.length * 10 +
            adventure?.result?.results.length * 1 -
            window.innerWidth
        );
        setComedyConstraint(
          window.innerWidth * 0.05 +
            comedy?.result?.results.length * 150 +
            comedy?.result?.results.length * 10 +
            comedy?.result?.results.length * 1 -
            window.innerWidth
        );
        setScificConstraint(
          window.innerWidth * 0.05 +
            scific?.result?.results.length * 150 +
            scific?.result?.results.length * 10 +
            scific?.result?.results.length * 1 -
            window.innerWidth
        );
        setThrillerConstraint(
          window.innerWidth * 0.05 +
            thriller?.result?.results.length * 150 +
            thriller?.result?.results.length * 10 +
            thriller?.result?.results.length * 1 -
            window.innerWidth
        );

        setAnimationConstraint(
          window.innerWidth * 0.05 +
            animation?.result?.results.length * 150 +
            animation?.result?.results.length * 10 +
            animation?.result?.results.length * 1 -
            window.innerWidth
        );

        setMysteryConstraint(
          window.innerWidth * 0.05 +
            mystery?.result.results.length * 150 +
            mystery?.result.results.length * 10 +
            mystery?.result.results.length * 1 -
            window.innerWidth
        );

        setDocumentaryConstraint(
          window.innerWidth * 0.05 +
            documentary?.result?.results.length * 150 +
            documentary?.result?.results.length * 10 +
            documentary?.result?.results.length * 1 -
            window.innerWidth
        );

        setWesternConstraint(
          window.innerWidth * 0.05 +
            western?.result?.results.length * 150 +
            western?.result?.results.length * 10 +
            western?.result?.results.length * 1 -
            window.innerWidth
        );
      }
    };

    updateConstraints();
    window.addEventListener("resize", updateConstraints);

    return () => {
      window.removeEventListener("resize", updateConstraints);
    };
  }, [
    trending,
    adventure,
    comedy,
    scific,
    thriller,
    animation,
    mystery,
    documentary,
    western,
  ]);

  const dispatch = useAppDispatch();
  const {
    trendingMovies,
    adventureMovies,
    comedyMovies,
    thrillerMovies,
    scificMovies,
    animationMovies,
    mysteryMovies,
    documentaryMovies,
    westernMovies,
  } = useAppSelector((state) => state?.mediaTypeSlice);

  const handleTrendingMovieClick = () => {
    if (trendingMovies) {
      return;
    }
    dispatch(setTrendingMovies(true));
  };

  const handleAdventureMovieClick = () => {
    if (adventureMovies) {
      return;
    }
    dispatch(setAdventureMovies(true));
  };

  const handleTrendingSeriesClick = () => {
    if (!trendingMovies) {
      return;
    }
    dispatch(setTrendingMovies(false));
  };

  const handleAdventureSeriesClick = () => {
    if (!adventureMovies) {
      return;
    }
    dispatch(setAdventureMovies(false));
  };

  const handleComedyMovieClick = () => {
    if (comedyMovies) {
      return;
    }
    dispatch(setComedyMovies(true));
  };

  const handleComedySeriesClick = () => {
    if (!comedyMovies) {
      return;
    }
    dispatch(setComedyMovies(false));
  };

  const handleScificMovieClick = () => {
    if (scificMovies) {
      return;
    }
    dispatch(setScificMovies(true));
  };

  const handleScificSeriesClick = () => {
    if (!scificMovies) {
      return;
    }
    dispatch(setScificMovies(false));
  };

  const handleThrillerMovieClick = () => {
    if (thrillerMovies) {
      return;
    }
    dispatch(setThrillerMovies(true));
  };

  const handleThrillerSeriesClick = () => {
    if (!thrillerMovies) {
      return;
    }
    dispatch(setThrillerMovies(false));
  };

  const handleAnimationMovieClick = () => {
    if (animationMovies) {
      return;
    }
    dispatch(setAnimationMovies(true));
  };

  const handleAnimationSeriesClick = () => {
    if (!animationMovies) {
      return;
    }
    dispatch(setAnimationMovies(false));
  };

  const handleMysteryMovieClick = () => {
    if (mysteryMovies) {
      return;
    }
    dispatch(setMysteryMovies(true));
  };

  const handleMysterySeriesClick = () => {
    if (!mysteryMovies) {
      return;
    }
    dispatch(setMysteryMovies(false));
  };

  const handleDocumentaryMovieClick = () => {
    if (documentaryMovies) {
      return;
    }
    dispatch(setDocumentaryMovies(true));
  };

  const handleDocumentarySeriesClick = () => {
    if (!documentaryMovies) {
      return;
    }
    dispatch(setDocumentaryMovies(false));
  };

  const handleWesternMovieClick = () => {
    if (westernMovies) {
      return;
    }
    dispatch(setWesternMovies(true));
  };

  const handleWesternSeriesClick = () => {
    if (!westernMovies) {
      return;
    }
    dispatch(setWesternMovies(false));
  };

  const navigate = useNavigate();

  type genreData = {
    genreData: {
      name: string;
      id: number;
    };
    navigatePath: string;
  };
  const handleNavigateToGenreType: (data: genreData) => void = (data) => {
    if (data?.navigatePath === "/movies") {
      dispatch(
        setMovieGenre({ movie: data?.genreData?.name, id: data?.genreData?.id })
      );
      navigate("/movies");
    } else {
      dispatch(
        setSeriesGenre({
          series: data?.genreData?.name,
          id: data?.genreData?.id,
        })
      );
      navigate("/series");
    }
  };

  return (
    <div className={classes.section_drag_cards} style={style}>
      <div className={classes.section_cards}>
        <DragCardsTitle
          // title="Trending Today"
          title="ယနေ့ခေတ်ရေပန်းစားနေသည်။"
          switchActive={trendingMovies}
          onMovieClick={handleTrendingMovieClick}
          onSeriesClick={handleTrendingSeriesClick}
          hideFilter={hideFilter}
        />

        {trendingLoading ? (
          <SkeletonCardsGrid />
        ) : trendingError ? (
          <ErrorCard error={trendingError} />
        ) : (
          <motion.div
            className={classes.section_card}
            style={{
              gridTemplateColumns: `repeat(${trending?.result?.results?.length}, 150px)`,
            }}
            drag="x"
            dragConstraints={{
              top: 0,
              left: -trendingContraint,
              right: 0,
              bottom: 0,
            }}
            transition={{
              type: "spring",
              damping: 100,
              stiffness: 100,
            }}
          >
            {trending?.result?.results?.length > 0 &&
              trending?.result?.results.map((data, idx) => {
                return (
                  <MovieCard
                    key={idx}
                    data={data}
                    navigateTo={
                      checkAndNavigate
                        ? trendingMovies
                          ? "movies"
                          : "series"
                        : navigateToMovies
                        ? "movies"
                        : "series"
                    }
                  />
                );
              })}
          </motion.div>
        )}

        {/* <div className={classes.title_container}>
          <div className={classes.title_sub_container}>
            <p className={classes.title}>Adventure</p>
            <div className={classes.switch}>
              <div
                className={`${classes.switch_card} ${
                  adventureMovies && classes.active
                }`}
                onClick={handleAdventureMovieClick}
              >
                <p>Movies</p>
              </div>
              <div
                className={`${classes.switch_card_2} ${
                  !adventureMovies && classes.active
                }`}
                onClick={handleAdventureSeriesClick}
              >
                <p>Series</p>
              </div>
            </div>
          </div>
          <p className={classes.view}>
            <i className={`pi pi-chevron-right ${classes.icon}`} />
          </p>
        </div> */}

        <DragCardsTitle
          title="စွန့်စားမှု"
          switchActive={adventureMovies}
          onMovieClick={handleAdventureMovieClick}
          onSeriesClick={handleAdventureSeriesClick}
          hideFilter={hideFilter}
          onArrowClick={() => {
            checkAndNavigate
              ? adventureMovies
                ? handleNavigateToGenreType({
                    genreData: {
                      name: "Adventure",
                      id: 12,
                    },
                    navigatePath: "/movies",
                  })
                : handleNavigateToGenreType({
                    genreData: {
                      name: "Adventure",
                      id: 10759,
                    },
                    navigatePath: "/series",
                  })
              : navigateToMovies
              ? handleNavigateToGenreType({
                  genreData: {
                    name: "Adventure",
                    id: 12,
                  },
                  navigatePath: "/movies",
                })
              : handleNavigateToGenreType({
                  genreData: {
                    name: "Adventure",
                    id: 10759,
                  },
                  navigatePath: "/series",
                });
          }}
        />

        {adventureLoading ? (
          <SkeletonCardsGrid />
        ) : adventureError ? (
          <ErrorCard error={adventureError} />
        ) : (
          <motion.div
            className={classes.section_card}
            style={{
              gridTemplateColumns: `repeat(${adventure?.result?.results?.length}, 150px)`,
            }}
            drag="x"
            dragConstraints={{
              top: 0,
              left: -adventureContraint,
              right: 0,
              bottom: 0,
            }}
            transition={{
              type: "spring",
              damping: 100,
              stiffness: 100,
            }}
          >
            {adventure?.result?.results.length > 0 &&
              adventure?.result?.results.map((data, idx) => {
                return (
                  <MovieCard
                    key={idx}
                    data={data}
                    navigateTo={
                      checkAndNavigate
                        ? adventureMovies
                          ? "movies"
                          : "series"
                        : navigateToMovies
                        ? "movies"
                        : "series"
                    }
                  />
                );
              })}
          </motion.div>
        )}

        {/* <div className={classes.title_container}>
          <div className={classes.title_sub_container}>
            <p className={classes.title}>Comedy</p>
            <div className={classes.switch}>
              <div
                className={`${classes.switch_card} ${
                  comedyMovies && classes.active
                }`}
                onClick={handleComedyMovieClick}
              >
                <p>Movies</p>
              </div>
              <div
                className={`${classes.switch_card_2} ${
                  !comedyMovies && classes.active
                }`}
                onClick={handleComedySeriesClick}
              >
                <p>Series</p>
              </div>
            </div>
          </div>
          <p className={classes.view}>
            <i className={`pi pi-chevron-right ${classes.icon}`} />
          </p>
        </div> */}

        <DragCardsTitle
          title="ဟာသ"
          switchActive={comedyMovies}
          onMovieClick={handleComedyMovieClick}
          onSeriesClick={handleComedySeriesClick}
          hideFilter={hideFilter}
          onArrowClick={() => {
            checkAndNavigate
              ? comedyMovies
                ? handleNavigateToGenreType({
                    genreData: {
                      name: "Comedy",
                      id: 35,
                    },
                    navigatePath: "/movies",
                  })
                : handleNavigateToGenreType({
                    genreData: {
                      name: "Comedy",
                      id: 35,
                    },
                    navigatePath: "/series",
                  })
              : navigateToMovies
              ? handleNavigateToGenreType({
                  genreData: {
                    name: "Comedy",
                    id: 35,
                  },
                  navigatePath: "/movies",
                })
              : handleNavigateToGenreType({
                  genreData: {
                    name: "Comedy",
                    id: 35,
                  },
                  navigatePath: "/series",
                });
          }}
        />

        {comedyLoading ? (
          <SkeletonCardsGrid />
        ) : comedyError ? (
          <ErrorCard error={comedyError} />
        ) : (
          <motion.div
            className={classes.section_card}
            style={{
              gridTemplateColumns: `repeat(${comedy?.result?.results.length}, 150px)`,
            }}
            drag="x"
            dragConstraints={{
              top: 0,
              left: -comedyContraint,
              right: 0,
              bottom: 0,
            }}
            transition={{
              type: "spring",
              damping: 100,
              stiffness: 100,
            }}
          >
            {comedy?.result?.results.length > 0 &&
              comedy?.result?.results?.map((data, idx) => {
                return (
                  <MovieCard
                    key={idx}
                    data={data}
                    navigateTo={
                      checkAndNavigate
                        ? comedyMovies
                          ? "movies"
                          : "series"
                        : navigateToMovies
                        ? "movies"
                        : "series"
                    }
                  />
                );
              })}
          </motion.div>
        )}

        <DragCardsTitle
          title="သိပ္ပံပညာ"
          switchActive={scificMovies}
          onMovieClick={handleScificMovieClick}
          onSeriesClick={handleScificSeriesClick}
          hideFilter={hideFilter}
          onArrowClick={() => {
            checkAndNavigate
              ? scificMovies
                ? handleNavigateToGenreType({
                    genreData: {
                      name: "Scific",
                      id: 878,
                    },
                    navigatePath: "/movies",
                  })
                : handleNavigateToGenreType({
                    genreData: {
                      name: "Scific",
                      id: 10765,
                    },
                    navigatePath: "/series",
                  })
              : navigateToMovies
              ? handleNavigateToGenreType({
                  genreData: {
                    name: "Scific",
                    id: 878,
                  },
                  navigatePath: "/movies",
                })
              : handleNavigateToGenreType({
                  genreData: {
                    name: "Scific",
                    id: 10765,
                  },
                  navigatePath: "/series",
                });
          }}
        />

        {scificLoading ? (
          <SkeletonCardsGrid />
        ) : scificError ? (
          <ErrorCard error={scificError} />
        ) : (
          <motion.div
            className={classes.section_card}
            style={{
              gridTemplateColumns: `repeat(${scific?.result?.results.length}, 150px)`,
            }}
            drag="x"
            dragConstraints={{
              top: 0,
              left: -scificContraint,
              right: 0,
              bottom: 0,
            }}
            transition={{
              type: "spring",
              damping: 100,
              stiffness: 100,
            }}
          >
            {scific?.result?.results.length > 0 &&
              scific?.result?.results.map((data, idx) => {
                return (
                  <MovieCard
                    key={idx}
                    data={data}
                    navigateTo={
                      checkAndNavigate
                        ? scificMovies
                          ? "movies"
                          : "series"
                        : navigateToMovies
                        ? "movies"
                        : "series"
                    }
                  />
                );
              })}
          </motion.div>
        )}

        <DragCardsTitle
          title="သည်းထိတ်ရင်ဖိုစရာ"
          switchActive={thrillerMovies}
          onMovieClick={handleThrillerMovieClick}
          onSeriesClick={handleThrillerSeriesClick}
          hideFilter={hideFilter}
          onArrowClick={() => {
            checkAndNavigate
              ? thrillerMovies
                ? handleNavigateToGenreType({
                    genreData: {
                      name: "Thriller",
                      id: 53,
                    },
                    navigatePath: "/movies",
                  })
                : handleNavigateToGenreType({
                    genreData: {
                      name: "Thriller",
                      id: 80,
                    },
                    navigatePath: "/series",
                  })
              : navigateToMovies
              ? handleNavigateToGenreType({
                  genreData: {
                    name: "Thriller",
                    id: 53,
                  },
                  navigatePath: "/movies",
                })
              : handleNavigateToGenreType({
                  genreData: {
                    name: "Thriller",
                    id: 80,
                  },
                  navigatePath: "/series",
                });
          }}
        />

        {thrillerLoading ? (
          <SkeletonCardsGrid />
        ) : thrillerError ? (
          <ErrorCard error={thrillerError} />
        ) : (
          <motion.div
            className={classes.section_card}
            style={{
              gridTemplateColumns: `repeat(${thriller?.result?.results.length}, 150px)`,
            }}
            drag="x"
            dragConstraints={{
              top: 0,
              left: -thrillerContraint,
              right: 0,
              bottom: 0,
            }}
            transition={{
              type: "spring",
              damping: 100,
              stiffness: 100,
            }}
          >
            {thriller?.result?.results.length > 0 &&
              thriller?.result?.results.map((data, idx) => {
                return (
                  <MovieCard
                    key={idx}
                    data={data}
                    navigateTo={
                      checkAndNavigate
                        ? thrillerMovies
                          ? "movies"
                          : "series"
                        : navigateToMovies
                        ? "movies"
                        : "series"
                    }
                  />
                );
              })}
          </motion.div>
        )}

        <DragCardsTitle
          title="ကာတွန်း"
          switchActive={animationMovies}
          onMovieClick={handleAnimationMovieClick}
          onSeriesClick={handleAnimationSeriesClick}
          hideFilter={hideFilter}
          onArrowClick={() => {
            checkAndNavigate
              ? animationMovies
                ? handleNavigateToGenreType({
                    genreData: {
                      name: "Animation",
                      id: 16,
                    },
                    navigatePath: "/movies",
                  })
                : handleNavigateToGenreType({
                    genreData: {
                      name: "Animation",
                      id: 16,
                    },
                    navigatePath: "/series",
                  })
              : navigateToMovies
              ? handleNavigateToGenreType({
                  genreData: {
                    name: "Animation",
                    id: 16,
                  },
                  navigatePath: "/movies",
                })
              : handleNavigateToGenreType({
                  genreData: {
                    name: "Animation",
                    id: 16,
                  },
                  navigatePath: "/series",
                });
          }}
        />

        {animationLoading ? (
          <SkeletonCardsGrid />
        ) : animationError ? (
          <ErrorCard error={animationError} />
        ) : (
          <motion.div
            className={classes.section_card}
            style={{
              gridTemplateColumns: `repeat(${animation?.result?.results?.length}, 150px)`,
            }}
            drag="x"
            dragConstraints={{
              top: 0,
              left: -animationContraint,
              right: 0,
              bottom: 0,
            }}
            transition={{
              type: "spring",
              damping: 100,
              stiffness: 100,
            }}
          >
            {animation?.result?.results.length > 0 &&
              animation?.result?.results.map((data, idx) => {
                return (
                  <MovieCard
                    key={idx}
                    data={data}
                    navigateTo={
                      checkAndNavigate
                        ? animationMovies
                          ? "movies"
                          : "series"
                        : navigateToMovies
                        ? "movies"
                        : "series"
                    }
                  />
                );
              })}
          </motion.div>
        )}

        <DragCardsTitle
          title="ဆန်းကြယ်သည်။"
          switchActive={mysteryMovies}
          onMovieClick={handleMysteryMovieClick}
          onSeriesClick={handleMysterySeriesClick}
          hideFilter={hideFilter}
          onArrowClick={() => {
            checkAndNavigate
              ? mysteryMovies
                ? handleNavigateToGenreType({
                    genreData: {
                      name: "Mystery",
                      id: 9648,
                    },
                    navigatePath: "/movies",
                  })
                : handleNavigateToGenreType({
                    genreData: {
                      name: "Mystery",
                      id: 9648,
                    },
                    navigatePath: "/series",
                  })
              : navigateToMovies
              ? handleNavigateToGenreType({
                  genreData: {
                    name: "Mystery",
                    id: 9648,
                  },
                  navigatePath: "/movies",
                })
              : handleNavigateToGenreType({
                  genreData: {
                    name: "Mystery",
                    id: 9648,
                  },
                  navigatePath: "/series",
                });
          }}
        />

        {mysteryLoading ? (
          <SkeletonCardsGrid />
        ) : mysteryError ? (
          <ErrorCard error={mysteryError} />
        ) : (
          <motion.div
            className={classes.section_card}
            style={{
              gridTemplateColumns: `repeat(${mystery?.result?.results.length}, 150px)`,
            }}
            drag="x"
            dragConstraints={{
              top: 0,
              left: -mysteryContraint,
              right: 0,
              bottom: 0,
            }}
            transition={{
              type: "spring",
              damping: 100,
              stiffness: 100,
            }}
          >
            {mystery?.result?.results.length > 0 &&
              mystery?.result?.results.map((data, idx) => {
                return (
                  <MovieCard
                    key={idx}
                    data={data}
                    navigateTo={
                      checkAndNavigate
                        ? mysteryMovies
                          ? "movies"
                          : "series"
                        : navigateToMovies
                        ? "movies"
                        : "series"
                    }
                  />
                );
              })}
          </motion.div>
        )}

        <DragCardsTitle
          title="မှတ်တမ်းရုပ်ရှင်"
          switchActive={documentaryMovies}
          onMovieClick={handleDocumentaryMovieClick}
          onSeriesClick={handleDocumentarySeriesClick}
          hideFilter={hideFilter}
          onArrowClick={() => {
            checkAndNavigate
              ? documentaryMovies
                ? handleNavigateToGenreType({
                    genreData: {
                      name: "Documentary",
                      id: 99,
                    },
                    navigatePath: "/movies",
                  })
                : handleNavigateToGenreType({
                    genreData: {
                      name: "Documentary",
                      id: 99,
                    },
                    navigatePath: "/series",
                  })
              : navigateToMovies
              ? handleNavigateToGenreType({
                  genreData: {
                    name: "Documentary",
                    id: 99,
                  },
                  navigatePath: "/movies",
                })
              : handleNavigateToGenreType({
                  genreData: {
                    name: "Documentary",
                    id: 99,
                  },
                  navigatePath: "/series",
                });
          }}
        />
        {documentaryLoading ? (
          <SkeletonCardsGrid />
        ) : documentaryError ? (
          <ErrorCard error={documentaryError} />
        ) : (
          <motion.div
            className={classes.section_card}
            style={{
              gridTemplateColumns: `repeat(${documentary?.result?.results.length}, 150px)`,
            }}
            drag="x"
            dragConstraints={{
              top: 0,
              left: -documentaryContraint,
              right: 0,
              bottom: 0,
            }}
            transition={{
              type: "spring",
              damping: 100,
              stiffness: 100,
            }}
          >
            {documentary?.result?.results.length > 0 &&
              documentary?.result?.results.map((data, idx) => {
                return (
                  <MovieCard
                    key={idx}
                    data={data}
                    navigateTo={
                      checkAndNavigate
                        ? documentaryMovies
                          ? "movies"
                          : "series"
                        : navigateToMovies
                        ? "movies"
                        : "series"
                    }
                  />
                );
              })}
          </motion.div>
        )}

        <DragCardsTitle
          title="အနောက်တိုင်း"
          switchActive={westernMovies}
          onMovieClick={handleWesternMovieClick}
          onSeriesClick={handleWesternSeriesClick}
          hideFilter={hideFilter}
          onArrowClick={() => {
            checkAndNavigate
              ? westernMovies
                ? handleNavigateToGenreType({
                    genreData: {
                      name: "Western",
                      id: 37,
                    },
                    navigatePath: "/movies",
                  })
                : handleNavigateToGenreType({
                    genreData: {
                      name: "Western",
                      id: 37,
                    },
                    navigatePath: "/series",
                  })
              : navigateToMovies
              ? handleNavigateToGenreType({
                  genreData: {
                    name: "Western",
                    id: 37,
                  },
                  navigatePath: "/movies",
                })
              : handleNavigateToGenreType({
                  genreData: {
                    name: "Western",
                    id: 37,
                  },
                  navigatePath: "/series",
                });
          }}
        />
        {westernLoading ? (
          <SkeletonCardsGrid />
        ) : westernError ? (
          <ErrorCard error={westernError} />
        ) : (
          <motion.div
            className={classes.section_card}
            style={{
              gridTemplateColumns: `repeat(${western?.result?.results.length}, 150px)`,
            }}
            drag="x"
            dragConstraints={{
              top: 0,
              left: -westernContraint,
              right: 0,
              bottom: 0,
            }}
            transition={{
              type: "spring",
              damping: 100,
              stiffness: 100,
            }}
          >
            {western?.result?.results.length > 0 &&
              western?.result?.results.map((data, idx) => {
                return (
                  <MovieCard
                    key={idx}
                    data={data}
                    navigateTo={westernMovies ? "movies" : "series"}
                  />
                );
              })}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default SectionDragCards;
